import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlow } from '../state/flowStore';
import { analyzeSkin, generateRoutine } from '../lib/api';
import { computeSkinAge, factorsFrom } from '../lib/skinAge';
import { displayName } from '../lib/ingredients';
import { ReviewCarousel } from '../components/ReviewCarousel';
import { TrustChips } from '../components/TrustChips';
import type { RoutineStep } from '../lib/types';

const STEPS = [
  'Cross-referencing 47 skin markers',
  'Reading visible texture patterns',
  'Assessing tone and pigmentation',
  'Matching against 12,000-product database',
  'Building your routine',
];

export function AnalysisLoadingScreen() {
  const nav = useNavigate();
  const flow = useFlow();
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run() {
    if (!flow.capturedImage) {
      setError('No photo was captured. Take a fresh photo to continue.');
      return;
    }

    const slowTicker = (async () => {
      for (const [i, delay] of [
        [0, 4000],
        [1, 4000],
      ] as const) {
        await sleep(delay);
        setCompleted(s => new Set(s).add(i));
        setCurrentStep(i + 1);
        setProgress((i + 1) / STEPS.length);
      }
    })();

    try {
      const scores = await analyzeSkin(flow.capturedImage);
      await slowTicker;

      setCompleted(s => {
        const next = new Set(s);
        for (let i = 0; i < 3; i++) next.add(i);
        return next;
      });
      setCurrentStep(3);
      setProgress(3 / STEPS.length);

      const skinAge = computeSkinAge(scores);
      const factors = factorsFrom(scores);
      const routine = await generateRoutine(scores, flow.realAge, flow.goals);

      const amRoutine: RoutineStep[] = routine.amSteps.map(s => ({
        name: stepName(s.ingredient, s.concentrationOrForm),
        detail: s.rationale,
        slug: s.ingredient,
      }));
      const pmRoutine: RoutineStep[] = routine.pmSteps.map(s => ({
        name: stepName(s.ingredient, s.concentrationOrForm),
        detail: s.rationale,
        slug: s.ingredient,
      }));

      const activeCategories = new Set(['serum', 'treatment', 'eye_cream']);
      const allActives = [...routine.amSteps, ...routine.pmSteps].filter(s =>
        activeCategories.has(s.category),
      );
      const seen = new Set<string>();
      const slugs: string[] = [];
      for (const s of allActives) {
        if (!seen.has(s.ingredient)) {
          seen.add(s.ingredient);
          slugs.push(s.ingredient);
        }
      }
      const prioritySlugs = slugs.slice(0, 5);
      const priorityIngredients = prioritySlugs.map(displayName);

      flow.setScanResult({
        skinAge,
        realAge: flow.realAge,
        scores,
        factors,
        amRoutine,
        pmRoutine,
        priorityIngredients,
        priorityIngredientSlugs: prioritySlugs,
        summary: routine.summary,
        expectedTimeline: routine.expectedResultsTimeline,
        warnings: routine.warnings,
      });

      setCompleted(new Set([0, 1, 2, 3, 4]));
      setCurrentStep(STEPS.length);
      setProgress(1);
      await sleep(400);
      nav('/reveal');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      setError(message);
    }
  }

  return (
    <div className="lume-bg min-h-svh flex flex-col">
      <div className="h-[1.5px] bg-surface relative">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent to-[#C89840] transition-all duration-300"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 gap-7">
        <h1 className="text-headline text-text">Analysing</h1>
        <ol className="flex flex-col">
          {STEPS.map((label, i) => {
            const state =
              completed.has(i) ? 'done' : i === currentStep ? 'active' : 'pending';
            return (
              <li key={label} className="flex items-center gap-3.5 py-3.5">
                <span className="w-5 flex justify-center">
                  {state === 'done' && <span className="text-accent">✓</span>}
                  {state === 'active' && (
                    <span className="h-3 w-3 rounded-full border-2 border-accent border-t-transparent animate-spin" />
                  )}
                  {state === 'pending' && <span className="h-1 w-1 rounded-full bg-surface-raised" />}
                </span>
                <span
                  className={`text-body ${
                    state === 'pending'
                      ? 'text-text-tertiary'
                      : state === 'active'
                      ? 'text-text'
                      : 'text-text-secondary'
                  }`}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>

        <ReviewCarousel />
        <TrustChips />
      </div>

      {error && (
        <div className="px-6 pb-10">
          <div className="lume-card-bordered text-center">
            <p className="text-body text-text">{error}</p>
            <button
              type="button"
              className="mt-3 lume-chip text-accent"
              onClick={() => nav('/camera')}
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function stepName(slug: string, conc?: string): string {
  const base = displayName(slug);
  return conc && conc.trim() ? `${base} ${conc}` : base;
}

function sleep(ms: number) {
  return new Promise(r => setTimeout(r, ms));
}
